/**
 * Stale-tab account guard (#3881).
 *
 * The session cookie is shared by every tab, but a tab's React state is not. A
 * tab showing A's form keeps running after B signs in elsewhere, and its saves
 * authenticate as B. So each tab remembers which account it is, names that
 * account on mutating requests, and the server refuses a mismatch (409
 * ACCOUNT_MISMATCH). Sign-in and sign-out also tell other tabs, which reload
 * when their account no longer matches.
 *
 * The tab's account is pinned, not read fresh from ['me'] on each request: a
 * refocus can refetch ['me'] as B while the form on screen still holds A's
 * data, and naming B then would wave that save through. A refetch that
 * disagrees reloads the tab, and so does a login in this tab that lands on a
 * different account: account-owned state lives in query caches and form fields,
 * and only a full page load is sure to end all of it. For the same reason the
 * pin survives going guest, so A then guest then B is still a change.
 *
 * Unbound until installAccountGuard runs in main.tsx, so the shared api modules
 * that import guardedFetch are a plain fetch on native. Keep this file free of
 * UI imports for that reason.
 */

import type { QueryClient } from '@tanstack/react-query';
import { toSafeRedirectPath } from './safeRedirect';

export const EXPECTED_USER_HEADER = 'X-Oro-Expected-User';
export const ACCOUNT_MISMATCH_CODE = 'ACCOUNT_MISMATCH';
export const ACCOUNT_MISMATCH_MESSAGE =
  'This tab is out of date, so nothing was saved. Reloading now.';

const SIGNAL_KEY = 'oro:account-signal';
const MISMATCH_RELOAD_DELAY_MS = 2000;

let tabAccount: string | null = null;
let notifyUser: ((message: string) => void) | null = null;
let reloadPending = false;

export class AccountMismatchError extends Error {
  readonly code = ACCOUNT_MISMATCH_CODE;
  readonly status = 409;

  constructor() {
    super(ACCOUNT_MISMATCH_MESSAGE);
    this.name = 'AccountMismatchError';
  }
}

export function isAccountMismatchError(error: unknown): boolean {
  return (error as { code?: unknown } | null)?.code === ACCOUNT_MISMATCH_CODE;
}

/** The header for a mutating request. Empty for a guest tab, as with old clients. */
export function expectedUserHeaders(): Record<string, string> {
  return tabAccount === null ? {} : { [EXPECTED_USER_HEADER]: tabAccount };
}

function reloadNow(): void {
  if (reloadPending) return;
  reloadPending = true;
  window.location.reload();
}

function reportAccountMismatch(): void {
  if (reloadPending || !notifyUser) return;
  reloadPending = true;
  notifyUser(ACCOUNT_MISMATCH_MESSAGE);
  window.setTimeout(() => window.location.reload(), MISMATCH_RELOAD_DELAY_MS);
}

/** Throws after telling the user, when a response is the server's refusal. */
export function assertNoAccountMismatch(status: number, bodyText: string): void {
  if (status !== 409) return;
  try {
    const body = JSON.parse(bodyText) as { error?: { code?: unknown } } | null;
    if (body?.error?.code !== ACCOUNT_MISMATCH_CODE) return;
  } catch {
    return; // Not the envelope, so not ours to interpret.
  }
  reportAccountMismatch();
  throw new AccountMismatchError();
}

/**
 * fetch for an API call that may write. Names the tab's account on mutating
 * requests and turns the server's refusal into AccountMismatchError, so a raw
 * upload site needs no handling of its own.
 */
export async function guardedFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const method = (init.method ?? 'GET').toUpperCase();
  const isMutation = method !== 'GET' && method !== 'HEAD';
  if (!isMutation || init.credentials === 'omit' || tabAccount === null) {
    return fetch(input, init);
  }

  const headers = new Headers(init.headers);
  headers.set(EXPECTED_USER_HEADER, tabAccount);
  const response = await fetch(input, { ...init, headers });
  if (response.status === 409) {
    assertNoAccountMismatch(409, await response.clone().text());
  }
  return response;
}

/**
 * Tells other tabs. The key is removed straight away, so no account id stays in
 * storage and every signal is a change. A storage event never fires in the tab
 * that wrote it, and nothing signals on page load, so a reload cannot trigger
 * another one.
 */
function broadcast(accountId: string | null): void {
  try {
    localStorage.setItem(SIGNAL_KEY, JSON.stringify({ accountId }));
    localStorage.removeItem(SIGNAL_KEY);
  } catch {
    // Private mode can refuse storage. The server still refuses a stale write.
  }
}

/** Call when THIS tab's session ends. The pin stays: see the file header. */
export function signalSignOut(): void {
  broadcast(null);
}

/**
 * Call when THIS tab signs in, before seeding ['me'], with the path the login
 * goes to next. Returns true when the tab held a different account: it is
 * already loading `destination`, so the caller stops there. Until the load
 * lands the pin still names the old account, so a save refuses rather than
 * sending the old account's fields as the new one. `destination` can be the
 * tab's own URL, so it is checked here, at the navigation (#3892).
 */
export function adoptLogin(accountId: string, destination: string): boolean {
  broadcast(accountId);
  if (!notifyUser) return false;
  if (tabAccount !== null && tabAccount !== accountId) {
    reloadPending = true;
    window.location.assign(toSafeRedirectPath(destination) ?? '/home');
    return true;
  }
  tabAccount = accountId;
  return false;
}

function handleStorage(event: StorageEvent): void {
  if (event.key !== SIGNAL_KEY || event.newValue === null) return;
  let accountId: unknown;
  try {
    accountId = (JSON.parse(event.newValue) as { accountId?: unknown }).accountId;
  } catch {
    return;
  }
  if (typeof accountId !== 'string' && accountId !== null) return;
  // A tab with no account holds nothing of anyone's, so it has no reason to reload.
  if (tabAccount !== null && accountId !== tabAccount) reloadNow();
}

/**
 * ['me'] is null for a guest. The pin survives that: a form from A can still be
 * on screen, and a later save must still name A.
 */
function trackMe(me: { id?: string } | null | undefined): void {
  const id = me?.id;
  if (!id) return;
  if (tabAccount === null) tabAccount = id;
  else if (id !== tabAccount) reloadNow();
}

/** Web only, once, at boot. Returns the teardown. */
export function installAccountGuard(
  queryClient: QueryClient,
  notify: (message: string) => void,
): () => void {
  notifyUser = notify;
  trackMe(queryClient.getQueryData<{ id?: string } | null>(['me']));
  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    if (
      event.type === 'updated' &&
      event.action.type === 'success' &&
      event.query.queryKey[0] === 'me'
    ) {
      trackMe(event.query.state.data as { id?: string } | null | undefined);
    }
  });
  window.addEventListener('storage', handleStorage);

  return () => {
    unsubscribe();
    window.removeEventListener('storage', handleStorage);
    notifyUser = null;
    tabAccount = null;
    reloadPending = false;
  };
}
